// src/v2/hooks/useReportViewState.js
//
// Shared hook for encoding report display state in the URL query string and
// persisting the last-seen state to localStorage.
//
// API:
//   const [state, setState] = useReportViewState({ storageKey, fields });
//
//   storageKey  — localStorage key (from portfolioStatePersistence.js constants)
//   fields      — Array of field descriptors: { param, valid?, validFn?, default, array? }
//                   param   — URL param name AND state key
//                   valid   — optional array of valid scalar values; values in this list
//                             are accepted from the URL and included in the URL
//                   validFn — optional function (value: string) => boolean; use instead
//                             of (or together with) `valid` for dynamic validation such
//                             as YYYY-MM month sort keys; takes precedence over `valid`
//                             when both are provided
//                   default — the default value (param omitted from URL when at default)
//                   array   — if true, value is a JS array serialised as comma-separated
//                             string in the URL; empty arrays omit the param
//
// Resolution order on mount (per-field):
//   1. URL param — validated against `valid` when provided; accepted if valid
//   2. localStorage value for that field — accepted as-is (no valid check)
//   3. Field default
//
// setState(patch) merges patch into state, writes the full merged state to
// localStorage, then rebuilds URLSearchParams (omitting params at default values
// or not in the valid list for scalar fields) and calls
// window.history.replaceState — no new history entry (Back exits the report).
//
// IMPORTANT: URL params are read ONCE on mount via a plain constructor call, not
// via useSearchParams(). This avoids the useSearchParams nav trap (see memory
// [useSearchParams nav trap in routed pages]).

import { useState, useCallback, useRef } from "react";
import {
  loadReportViewState,
  saveReportViewState,
} from "../../services/portfolioStatePersistence.js";

export function useReportViewState({ storageKey, fields }) {
  // Capture fields once so useCallback deps stay stable when the caller
  // passes a module-level constant array (the common case).
  const fieldsRef = useRef(fields);

  const [state, setStateInternal] = useState(() => {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      params = new URLSearchParams();
    }
    const saved = loadReportViewState(storageKey) ?? {};
    const initial = {};

    for (const f of fieldsRef.current) {
      if (f.array) {
        // Array field: comma-separated in URL, JS array in state.
        const rawUrl = params.get(f.param);
        if (rawUrl !== null && rawUrl !== "") {
          initial[f.param] = rawUrl.split(",").filter(Boolean);
        } else if (Array.isArray(saved[f.param])) {
          initial[f.param] = saved[f.param];
        } else {
          initial[f.param] = Array.isArray(f.default) ? [...f.default] : [];
        }
      } else {
        // Scalar field.
        const rawUrl = params.get(f.param);
        if (rawUrl !== null && rawUrl !== "") {
          // URL param present — validate using validFn (preferred) or valid list.
          const urlValid = f.validFn
            ? f.validFn(rawUrl)
            : (!f.valid || f.valid.includes(rawUrl));
          if (urlValid) {
            initial[f.param] = rawUrl;
          } else {
            // URL value invalid — fall to localStorage or default.
            const lsVal = saved[f.param];
            initial[f.param] = lsVal !== undefined ? lsVal : (f.default ?? "");
          }
        } else {
          // No URL param — use localStorage value if present, else default.
          const lsVal = saved[f.param];
          initial[f.param] = lsVal !== undefined ? lsVal : (f.default ?? "");
        }
      }
    }
    return initial;
  });

  const setState = useCallback(
    (patch) => {
      setStateInternal((prev) => {
        const next = { ...prev, ...patch };

        // Persist the full state to localStorage.
        saveReportViewState(storageKey, next);

        // Build URL query string from the merged state.
        // Params equal to their defaults, or scalar values not in their valid
        // list, are omitted so the URL stays clean.
        const urlParams = new URLSearchParams();
        for (const f of fieldsRef.current) {
          const val = next[f.param];
          if (f.array) {
            if (Array.isArray(val) && val.length > 0) {
              urlParams.set(f.param, val.join(","));
            }
          } else {
            const def = f.default ?? "";
            if (val !== def && val !== null && val !== undefined) {
              // Include in URL if the value passes the validator.
              // validFn takes precedence; falls back to checking the valid list;
              // if neither is set, all non-default values are included.
              const shouldInclude = f.validFn
                ? f.validFn(String(val))
                : (!f.valid || f.valid.includes(val));
              if (shouldInclude) {
                urlParams.set(f.param, String(val));
              }
            }
          }
        }

        const qs = urlParams.toString();
        try {
          window.history.replaceState(
            null,
            "",
            qs
              ? "?" + qs
              : window.location.pathname + (window.location.hash || ""),
          );
        } catch {
          // replaceState can throw in some test environments; ignore.
        }

        return next;
      });
    },
    [storageKey],
  );

  return [state, setState];
}
